import React, { useState, useEffect, useCallback, useContext } from 'react';
import AttachmentsComponent from './AttachmentsComponent';
import {
  useQueryWithSimpleErrorHandling,
  useClientQueryWithSimpleErrorHandling,
  useToast,
  useAuthUserData,
  useSimpleError,
  useAuthorization
} from 'hooks';
import { useConditionalSubscription, useMutation } from 'features/common/security/hooks';
import { useFileDownload, useFileUpload } from 'hooks/fileHandling';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { env } from 'utils/env';
import { emptyString } from 'constants/defaultValues';
import { entityTypes, docTypes } from '../../constants';
import { AVAILABLE_DOCUMENT_TRANSITIONS } from '../queries';
import { PROPERTY_DETAILS } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { RETRIEVE_ATTACHMENTS, UPDATE_ATTACHMENT, DELETE_ATTACHMENT, CHANGE_ATTACHMENT_STATE } from '../mutations';
import {
  ATTACHMENTS_RETRIEVED,
  ATTACHMENTS_ADDED,
  ATTACHMENT_UPDATED,
  ATTACHMENT_DELETED,
  ATTACHMENT_STATE_CHANGED
} from '../subscriptions';
import PropTypes from 'prop-types';
import RequiredDocTypeListContainer from './RequiredDocTypeListContainer';
import FileHistoryContainer from '../../history/components/FileHistoryContainer';
import DocumentTransitionsContainer from './DocumentTransitionsContainer';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const getUpdateProperties = properties => properties.map(({ propertyId, value }) => ({ propertyId, propertyValue: value }));

const AttachmentsContainer = ({ documentId }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToViewUploadDocuments = useAuthorization(AuthorizationCodes.hasRightToViewUploadDocuments, documentTypeId, documentStateId);
  const addToast = useToast();
  const { t } = useTranslation();
  const showError = useSimpleError();
  const { authUser } = useAuthUserData();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithSimpleErrorHandling();

  const [attachments, setAttachments] = useState([]);
  const [availableDocumentTransitions, setAvailableDocumentTransitions] = useState([]);

  const [expanded, setExpanded] = useState({});
  const [showRequiredDocTypes, setShowRequiredDocTypes] = useState(false);
  const [showDocStateTransitions, setShowDocStateTransitions] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState({});
  const [attachmentIdInProgress, setAttachmentIdInProgress] = useState();
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  /* Attachment Retrieval */

  const [retrieveAttachments] = useMutation(RETRIEVE_ATTACHMENTS, {
    onError: error => {
      showError(error);
      setAttachmentsLoading(false);
    }
  });

  useEffect(() => {
    if (!documentId) return;
    setAttachmentsLoading(true);
    retrieveAttachments({
      variables: {
        input: {
          documentId
        }
      }
    });
  }, [retrieveAttachments, documentId]);

  /* End Attachment Retrieval */

  const { loading: transitionsLoading, error: transitionsError } = useQueryWithSimpleErrorHandling(AVAILABLE_DOCUMENT_TRANSITIONS, {
    onCompleted: ({ availableDocumentTransitions }) => {
      setAvailableDocumentTransitions(availableDocumentTransitions);
    }
  });

  const changeActionInProgress = useCallback((documentAttachmentId, actionInProgress, withLensUpdate, updatedAttachmentData) => {
    setAttachments(prevState =>
      prevState.map(attachment =>
        attachment.documentAttachmentId === documentAttachmentId
          ? { ...(updatedAttachmentData ?? attachment), actionInProgress, withLensUpdate: withLensUpdate ?? false }
          : attachment
      )
    );
  }, []);

  /* Attachment Cards */

  const handleExpandedChange = useCallback(
    documentAttachmentId => {
      var newExpanded = expanded[documentAttachmentId] ?? false;
      setExpanded({ ...expanded, [documentAttachmentId]: !newExpanded });
    },
    [expanded]
  );

  /* End Attachment Cards */

  /* Update */

  // Save

  const [updateAttachment] = useMutation(UPDATE_ATTACHMENT, {
    onError: error => {
      showError(error);
      changeActionInProgress(attachmentIdInProgress, false);
    }
  });

  const handleSave = useCallback(
    ({ documentAttachmentId, validityDate, attachmentComment, properties }) => {
      setAttachmentIdInProgress(documentAttachmentId);
      changeActionInProgress(documentAttachmentId, true);
      updateAttachment({
        variables: {
          input: { documentAttachmentId, validityDate, attachmentComment, properties: getUpdateProperties(properties) }
        }
      });
    },
    [changeActionInProgress, updateAttachment]
  );

  // Change state

  const [changeAttachmentState] = useMutation(CHANGE_ATTACHMENT_STATE, {
    onError: error => {
      showError(error);
      changeActionInProgress(attachmentIdInProgress, false);
    }
  });

  const handleChangeState = useCallback(
    (documentAttachmentId, stateId) => {
      setAttachmentIdInProgress(documentAttachmentId);
      changeActionInProgress(documentAttachmentId, true);
      changeAttachmentState({
        variables: { input: { documentAttachmentId, stateId } }
      });
    },
    [changeActionInProgress, changeAttachmentState]
  );

  /* End Update */

  /* Delete */

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT, {
    onError: error => showError(error)
  });

  const handleDelete = useCallback(
    documentAttachmentId => {
      changeActionInProgress(documentAttachmentId, true);
      deleteAttachment({
        variables: { input: { documentAttachmentId } }
      });
    },
    [changeActionInProgress, deleteAttachment]
  );

  /* End Delete */

  /* Upload */

  const onUploadCompleted = useCallback(
    ({ fileUniqueId, uploadedDate, documentAttachmentId }) => {
      updateAttachment({
        variables: { input: { documentAttachmentId, fileUniqueId, uploadedDate } }
      });
    },
    [updateAttachment]
  );

  const [uploadFile, { maxSize }] = useFileUpload({
    onCompleted: onUploadCompleted,
    onError: (error, params) => {
      showError(error);
      changeActionInProgress(params.documentAttachmentId, false);
    },
    showNotifications: false
  });

  const handleUploadFile = useCallback(
    async (documentAttachmentId, file) => {
      const documentAttachment = attachments.find(x => x.documentAttachmentId === documentAttachmentId);
      const {
        fileUniqueId,
        contentType,
        entityType,
        entityTitle,
        entityPK1,
        entityPK2,
        requiredDocumentName: requiredDocName,
        validityDate: validTo
      } = documentAttachment;

      if (!contentType) {
        addToast(t('Offer.Edit.AttachmentsData.ContentTypeMissing', { requiredDocName }), 'error', {
          toastId: 'attachmentContentTypeMissingToast'
        });
        return;
      }

      changeActionInProgress(documentAttachmentId, true);
      uploadFile(
        file,
        {
          sharepointUniqueID: fileUniqueId,
          contentType,
          appCode: env.REACT_APP_CODE,
          userId: parseInt(authUser?.userId),
          dmsFileName: contentType + '.' + file.name.split('.').pop(),
          fileName: file.name,
          context: entityType,
          entityPK1,
          entityPK2,
          documentId,
          assetId: entityType === entityTypes.ASSET ? entityPK1 : null,
          entityTitle,
          validTo
        },
        { documentAttachmentId }
      );
    },
    [addToast, attachments, authUser, changeActionInProgress, documentId, t, uploadFile]
  );

  /* End Upload */

  /* Download */

  const [downloadFile] = useFileDownload();

  const handleDownloadFile = useCallback(
    fileUniqueId => {
      if (fileUniqueId) downloadFile(fileUniqueId);
    },
    [downloadFile]
  );

  /* End Download */

  /* Modals */

  const toggleRequiredDocTypesModal = useCallback(show => {
    if (show) {
      setShowRequiredDocTypes(true);
    } else {
      setShowRequiredDocTypes(false);
    }
  }, []);

  const toggleDocStateTransitionsModal = useCallback(show => {
    if (show) {
      setShowDocStateTransitions(true);
    } else {
      setShowDocStateTransitions(false);
    }
  }, []);

  const toggleHistory = useCallback(show => {
    if (show) {
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  }, []);

  /* End Modals */

  /* Properties */

  const loadPropertyDetails = useCallback(
    async propertyId => {
      const {
        data: { propertyDetails }
      } = await clientQuery(PROPERTY_DETAILS, {
        variables: { propertyId }
      });
      return new Promise((resolve, _) => resolve(propertyDetails));
    },
    [clientQuery]
  );

  /* End Properties */

  /* Subscriptions */

  useConditionalSubscription(ATTACHMENTS_RETRIEVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { attachmentsRetrieved }
      }
    }) => {
      if (attachmentsRetrieved?.attachmentData) {
        setAttachments(
          attachmentsRetrieved.attachmentData.attachments.map(x => ({
            ...x,
            actionInProgress: false,
            withLensUpdate: true
          }))
        );
      }
      setAttachmentsLoading(false);
    }
  });

  useConditionalSubscription(ATTACHMENTS_ADDED, {
    onData: ({
      data: {
        data: { attachmentsAdded }
      }
    }) => {
      toggleRequiredDocTypesModal(false);
      setAttachments(prevState => {
        const addedAttachments = attachmentsAdded.attachmentData.attachments.map(x => ({
          ...x,
          actionInProgress: false,
          withLensUpdate: true
        }));
        return [...prevState, ...addedAttachments];
      });

      addToast(t('General.Saved'), 'success', { toastId: 'attachmentAddedToast' });
    }
  });

  useConditionalSubscription(ATTACHMENT_UPDATED, {
    onData: ({
      data: {
        data: { attachmentUpdated }
      }
    }) => {
      changeActionInProgress(attachmentUpdated.attachment.documentAttachmentId, false, true, attachmentUpdated.attachment);
      addToast(t('General.Saved'), 'success', { toastId: 'attachmentUpdatedToast' });
    }
  });

  useConditionalSubscription(ATTACHMENT_DELETED, {
    onData: ({
      data: {
        data: { attachmentDeleted }
      }
    }) => {
      setAttachments(prevState =>
        prevState.filter(attachment => attachment.documentAttachmentId !== attachmentDeleted.documentAttachmentId)
      );
      client.cache.evict({ id: `Attachment:{"documentAttachmentId":${attachmentDeleted.documentAttachmentId}}` });
      addToast(t('General.Deleted'), 'success', { toastId: 'attachmentDeletedToast' });
    }
  });

  useConditionalSubscription(ATTACHMENT_STATE_CHANGED, {
    onData: ({
      data: {
        data: { attachmentStateChanged }
      }
    }) => {
      changeActionInProgress(attachmentStateChanged.attachment.documentAttachmentId, false, true, attachmentStateChanged.attachment);
      addToast(t('Offer.Edit.AttachmentsData.StateChanged'), 'success', { toastId: 'attachmentStateChangedToast' });
    }
  });

  /* End Subscriptions */

  return (
    <>
      {hasRightToViewUploadDocuments && (
        <AttachmentsComponent
          attachments={attachments}
          maxSize={maxSize}
          availableDocumentTransitions={availableDocumentTransitions}
          loading={attachmentsLoading || transitionsLoading}
          error={transitionsError || emptyString}
          onSave={handleSave}
          onChangeState={handleChangeState}
          onDelete={handleDelete}
          onUploadFile={handleUploadFile}
          onDownloadFile={handleDownloadFile}
          onExpandedChange={handleExpandedChange}
          expanded={expanded}
          onSelectAttachment={setSelectedAttachment}
          onOpenRequiredDocTypesModal={() => toggleRequiredDocTypesModal(true)}
          onToggleDocStateTransitionsModal={toggleDocStateTransitionsModal}
          onToggleHistory={toggleHistory}
          loadPropertyDetails={loadPropertyDetails}
        />
      )}
      {showRequiredDocTypes && (
        <RequiredDocTypeListContainer documentId={documentId} onCloseModal={() => toggleRequiredDocTypesModal(false)} />
      )}
      {showDocStateTransitions && selectedAttachment && (
        <DocumentTransitionsContainer
          documentAttachmentId={selectedAttachment.documentAttachmentId}
          onCloseModal={() => toggleDocStateTransitionsModal(false)}
        />
      )}
      {showHistory && selectedAttachment && (
        <FileHistoryContainer
          fileId={selectedAttachment.fileUniqueId}
          docType={docTypes.ATTACHMENT}
          onDownloadFile={handleDownloadFile}
          onCloseModal={() => toggleHistory(false)}
        />
      )}
    </>
  );
};

AttachmentsContainer.propTypes = {
  documentId: PropTypes.number.isRequired
};

export default AttachmentsContainer;
